import { DataTableQueryContextProvider } from "@/components/data-table/DataTableQueryContext";
import { useCreateDataTableOptions } from "@/components/data-table/hooks/useCreateDataTableOptions";
import { List } from "@/components/list/List";
import Title from "@/components/Title";
import { Module } from "@/constants";
import { readStore } from "@/schemas/store";
import { SuccessFetchListResponse } from "@/services/api";
import { ProjectLimitSummaryStoreHours } from "@/types/projectLimit";
import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
} from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ProjectLimitSummaryService } from "../../service/projectLimitSummary";
import { ProjectLimitSummaryHoursColumnDef } from "../../types/limits";
import useProjectLimitQueryInvalidator from "./hooks/useProjectLimitQueryInvalidator";
import ProjectLimitMonthRangeFilter from "./ProjectLimitMonthRangeFilter";
import ProjectLimitSummaryFooter from "./ProjectLimitSummaryFooter";
import { MonthRange } from "./types";
import { Row } from "@tanstack/react-table";
import { useProjectId } from "@/modules/projects/hooks/useProjectId";
import { formatDate, formatNumber } from "@/lib/utils/formatters";
import useQueryData from "@/hooks/useQueryData";

// TODO: add support for this when user click "Reset Columns" menu item
const initialColumnVisibility = {
  // Hide all columns by default
  ...Object.fromEntries(
    Object.keys(readStore.unwrap().shape).map((key) => [key, false]),
  ),
  // Show the following columns by default
  ref_number: true,
  client_store_number: true,
  name: true,
};

const currentYear = new Date().getFullYear();
const startOfYear = new Date(`${currentYear}-01-01`);
const endOfYear = new Date(`${currentYear}-12-31`);

const ProjectLimitSummary = () => {
  const name = "projectLimitSummary";
  const { t } = useTranslation();
  const projectId = useProjectId();

  const projectLimitQueryInvalidator = useProjectLimitQueryInvalidator();

  const [selectedMonthRange, setSelectedMonthRange] = useState<MonthRange>({
    from: startOfYear.toISOString(),
    to: endOfYear.toISOString(),
  });

  const options =
    useCreateDataTableOptions<ProjectLimitSummaryStoreHours>(name);

  const start = formatDate(selectedMonthRange.from);
  const end = formatDate(selectedMonthRange.to);

  const storeHoursQuery = useInfiniteQuery({
    queryKey: [Module.ProjectLimits, projectId, "store-hours", start, end],
    queryFn: ({ pageParam }) =>
      ProjectLimitSummaryService.getStoreHours(projectId, start, end, {
        lastItem: pageParam,
      }),
    initialPageParam: undefined,
    getNextPageParam: (
      lastPage: SuccessFetchListResponse<ProjectLimitSummaryStoreHours>,
    ) => lastPage.last_item,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
  });

  const data = useQueryData(storeHoursQuery);

  const noData = data.length === 0;

  const periodsSummaryQuery = useQuery({
    queryKey: [Module.ProjectLimits, projectId, "periods-summary", start, end],
    queryFn: () =>
      ProjectLimitSummaryService.getPeriodsSummary(projectId, start, end),
  });

  const queryValue = useMemo(
    () => ({ query: storeHoursQuery }),
    [storeHoursQuery],
  );

  useEffect(() => {
    projectLimitQueryInvalidator.invalidateAll();
  }, [selectedMonthRange, projectLimitQueryInvalidator]);

  const hoursColumns = useMemo(() => {
    if (noData) {
      return {};
    }
    const firstRecord = data[0];
    return Object.keys(firstRecord?.hours ?? {}).reduce(
      (acc, key) => {
        acc[key] = {
          id: key,
          header: key,
          accessorKey: key,
          enableHiding: false,
          enablePinning: false,
          enableSorting: false,
          enableColumnFilter: false,
          meta: {
            isHoursColumn: true,
          },
          cell: ({ row }) => {
            return formatNumber(
              (row.original as unknown as Record<string, string>)[key] ?? "",
            );
          },
        };
        return acc;
      },
      {} as Record<string, ProjectLimitSummaryHoursColumnDef>,
    );
  }, [data, noData]);

  const dataMapper = useCallback((data: ProjectLimitSummaryStoreHours[]) => {
    return data.map((item) => ({
      ...item,
      ...item.hours,
    }));
  }, []);

  const tableFeatures = useMemo(
    () => ({
      initialColumnVisibility,
      enableColumnOrder: false,
      enableColumnPinning: false,
      enableSorting: false,
      enableFilters: false,
    }),
    [],
  );

  const columnOverrides = useMemo(() => {
    return {
      ...hoursColumns,
      status: {
        id: "status",
        cell: ({ row }: { row: Row<ProjectLimitSummaryStoreHours> }) =>
          t(`storeStatuses.${row.original.status}`),
      },
    };
  }, [hoursColumns, t]);

  const isFetching =
    storeHoursQuery.isFetching || periodsSummaryQuery.isFetching;

  return (
    <List
      schema={readStore}
      name="projectLimitsSummary"
      translationKey="projectLimits"
      query={storeHoursQuery}
      options={options}
      columnOverrides={columnOverrides}
      dataMapper={dataMapper}
      tableFeatures={tableFeatures}
    >
      <Title>{t("projectLimits.summary.title")}</Title>
      <div className="flex flex-row gap-2 justify-between items-center py-2">
        <List.Menubar className="flex-1 m-0">
          <List.Menubar.Tools />
          <List.Menubar.Columns />
          <List.Menubar.Reset />
        </List.Menubar>
        <ProjectLimitMonthRangeFilter
          value={selectedMonthRange}
          onMonthRangeChange={setSelectedMonthRange}
          className="shrink-0 grow-0"
          popoverClassName="z-100"
        />
      </div>
      <DataTableQueryContextProvider value={queryValue}>
        <List.Content
          footer={
            noData ? null : (
              <ProjectLimitSummaryFooter
                query={periodsSummaryQuery}
                isFetching={isFetching}
                selectedMonthRange={selectedMonthRange}
              />
            )
          }
        >
          <List.Status />
        </List.Content>
      </DataTableQueryContextProvider>
    </List>
  );
};

export default ProjectLimitSummary;
